import { useEffect, useMemo, useState } from "react";
import { Card, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import ApiService from "../../../core/service/api-service";
import DataGrid, { getGridBodyHeight } from "../../../core/components/data-grid/DataGrid";
import { DataGridProvider, useDataGrid } from "../../../core/components/data-grid/DataGridContext";
import { useRowDetailDrawer } from "../../../core/components/data-grid/useRowDetailDrawer";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import reportBillingColumns from "./ReportBillingColumns";
import ReportBillingTools from "./ReportBillingTools";
import ReportBillingLogsGrid from "../ReportBillingLogsGrid";
import ReconciliationModal from "../reconciliation/ReconciliationModal";

export default function ReportBillingGrid() {
    const { t } = useTranslation();
    const { hasPermission } = useUser();
    const {
        setDataGridProps, queryModel, setData, setPaginationResult, loadCount, refreshDataGrid,
    } = useDataGrid();

    const [reconciliationOpen, setReconciliationOpen] = useState(false);

    const columns = useMemo(() => reportBillingColumns(t), []);
    const canSeeHistory = hasPermission(PERMISSIONS.REPORT_BILLING_HISTORY);

    // Satır genişletme: geçmiş, mevcut detay çekmecesi mekanizmasıyla kendi grid'inde açılır.
    const { expandColumn, drawer } = useRowDetailDrawer({
        name: 'report-billing',
        columns,
        getTitle: (record) => (
            `${record.patientName || '-'} · ${record.accessionNumber || record.id}`
        ),
        renderDetail: (record) => (
            canSeeHistory ? (
                <Card size={'small'}>
                    <Tabs size={'small'}>
                        <Tabs.TabPane key={`billing-logs-${record.id}`} tab={t('Billing History')}>
                            <DataGridProvider name={'report-billing-logs-provider'}>
                                <ReportBillingLogsGrid reportId={record.id} />
                            </DataGridProvider>
                        </Tabs.TabPane>
                    </Tabs>
                </Card>
            ) : null
        ),
    });

    const initDataGrid = async () => {
        const response = await ApiService.ReportBilling.GetDataGrid(queryModel);

        if (!response.success)
            return;

        setData(response.data);
        setPaginationResult(response.pagination);

        // Sayaç ayrı istek, sayfa çizildikten SONRA (rapor/çekim ekranlarıyla aynı desen).
        loadCount(
            (exact) => ApiService.ReportBilling.GetDataGridCount(queryModel, exact),
            JSON.stringify(queryModel.filter)
        );
    }

    useEffect(() => {
        if (window.reportBillingGridKey !== queryModel.updateKey) {
            window.reportBillingGridKey = queryModel.updateKey;
            initDataGrid();
        }
    }, [queryModel.updateKey]);

    useEffect(() => {
        const gridColumns = canSeeHistory ? [expandColumn, ...columns] : columns;

        setDataGridProps({
            rowKey: 'id',
            columns: gridColumns,
            enableRowSelection: true,
            virtual: true,
            antdTableProps: {
                // +40: seçim kolonu. Sabit gövde yüksekliği satırların sanallaşmasını sağlar.
                scroll: {
                    x: 40 + gridColumns.map(x => x.width || 0).reduce((a, b) => a + b, 0),
                    y: getGridBodyHeight()
                },
                tableLayout: 'fixed'
            },
        })
    }, []);

    return (
        <>
            <DataGrid
                name={'report-billing'}
                title={() => (
                    <ReportBillingTools onOpenReconciliation={() => setReconciliationOpen(true)} />
                )}
            />
            {drawer}
            <ReconciliationModal
                open={reconciliationOpen}
                onClose={() => setReconciliationOpen(false)}
                onApplied={refreshDataGrid}
            />
        </>
    )
}
